export interface TimelineTooltipAnchor {
  anchorX: number;
  anchorY: number;
  preferAbove: boolean;
}

export interface TimelineTooltipViewportState {
  scrollLeft: number;
  scrollTop: number;
  width: number;
  height: number;
}

export interface TimelineTooltipDimensions {
  width: number;
  height: number;
}

export interface TimelineTooltipPosition {
  left: number;
  top: number;
}

interface TooltipVerticalFit {
  canPlaceAbove: boolean;
  canPlaceBelow: boolean;
}

const TOOLTIP_GAP = 10;
const TOOLTIP_VIEWPORT_PADDING = 8;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function hasMeasuredViewport(viewportState: TimelineTooltipViewportState): boolean {
  return viewportState.width > 0 && viewportState.height > 0;
}

function getViewportAnchor(input: {
  tooltip: TimelineTooltipAnchor;
  viewportState: TimelineTooltipViewportState;
}): { x: number; y: number } {
  return {
    x: input.tooltip.anchorX - input.viewportState.scrollLeft,
    y: input.tooltip.anchorY - input.viewportState.scrollTop,
  };
}

function getMaxViewportOffset(input: {
  viewportSize: number;
  tooltipSize: number;
}): number {
  return Math.max(
    TOOLTIP_VIEWPORT_PADDING,
    input.viewportSize - input.tooltipSize - TOOLTIP_VIEWPORT_PADDING,
  );
}

function getViewportLeft(input: {
  anchorX: number;
  viewportWidth: number;
  tooltipWidth: number;
}): number {
  return clamp(
    input.anchorX - input.tooltipWidth / 2,
    TOOLTIP_VIEWPORT_PADDING,
    getMaxViewportOffset({
      viewportSize: input.viewportWidth,
      tooltipSize: input.tooltipWidth,
    }),
  );
}

function canFitTooltipInSpace(input: {
  space: number;
  tooltipHeight: number;
}): boolean {
  return input.space >= input.tooltipHeight + TOOLTIP_GAP;
}

function getTooltipVerticalFit(input: {
  viewportAnchorY: number;
  viewportHeight: number;
  tooltipHeight: number;
}): TooltipVerticalFit {
  const spaceAbove = input.viewportAnchorY - TOOLTIP_VIEWPORT_PADDING;
  const spaceBelow =
    input.viewportHeight - input.viewportAnchorY - TOOLTIP_VIEWPORT_PADDING;

  return {
    canPlaceAbove: canFitTooltipInSpace({
      space: spaceAbove,
      tooltipHeight: input.tooltipHeight,
    }),
    canPlaceBelow: canFitTooltipInSpace({
      space: spaceBelow,
      tooltipHeight: input.tooltipHeight,
    }),
  };
}

function shouldFlipTooltipBelow(input: {
  tooltip: TimelineTooltipAnchor;
  fit: TooltipVerticalFit;
}): boolean {
  return (
    input.tooltip.preferAbove &&
    !input.fit.canPlaceAbove &&
    input.fit.canPlaceBelow
  );
}

function shouldFlipTooltipAbove(input: {
  tooltip: TimelineTooltipAnchor;
  fit: TooltipVerticalFit;
}): boolean {
  return (
    !input.tooltip.preferAbove &&
    !input.fit.canPlaceBelow &&
    input.fit.canPlaceAbove
  );
}

function shouldPlaceTooltipAbove(input: {
  tooltip: TimelineTooltipAnchor;
  viewportAnchorY: number;
  viewportHeight: number;
  tooltipHeight: number;
}): boolean {
  const fit = getTooltipVerticalFit({
    viewportAnchorY: input.viewportAnchorY,
    viewportHeight: input.viewportHeight,
    tooltipHeight: input.tooltipHeight,
  });

  if (shouldFlipTooltipBelow({ tooltip: input.tooltip, fit })) {
    return false;
  }

  if (shouldFlipTooltipAbove({ tooltip: input.tooltip, fit })) {
    return true;
  }

  return input.tooltip.preferAbove;
}

function getViewportTop(input: {
  tooltip: TimelineTooltipAnchor;
  viewportAnchorY: number;
  viewportHeight: number;
  tooltipHeight: number;
}): number {
  const placeAbove = shouldPlaceTooltipAbove({
    tooltip: input.tooltip,
    viewportAnchorY: input.viewportAnchorY,
    viewportHeight: input.viewportHeight,
    tooltipHeight: input.tooltipHeight,
  });
  const preferredTop = placeAbove
    ? input.viewportAnchorY - input.tooltipHeight - TOOLTIP_GAP
    : input.viewportAnchorY + TOOLTIP_GAP;

  return clamp(
    preferredTop,
    TOOLTIP_VIEWPORT_PADDING,
    getMaxViewportOffset({
      viewportSize: input.viewportHeight,
      tooltipSize: input.tooltipHeight,
    }),
  );
}

export function getTimelineTooltipPosition(input: {
  tooltip: TimelineTooltipAnchor | null;
  viewportState: TimelineTooltipViewportState;
  dimensions: TimelineTooltipDimensions;
}): TimelineTooltipPosition | null {
  if (!input.tooltip || !hasMeasuredViewport(input.viewportState)) {
    return null;
  }

  const viewportAnchor = getViewportAnchor({
    tooltip: input.tooltip,
    viewportState: input.viewportState,
  });

  return {
    left:
      getViewportLeft({
        anchorX: viewportAnchor.x,
        viewportWidth: input.viewportState.width,
        tooltipWidth: input.dimensions.width,
      }) + input.viewportState.scrollLeft,
    top:
      getViewportTop({
        tooltip: input.tooltip,
        viewportAnchorY: viewportAnchor.y,
        viewportHeight: input.viewportState.height,
        tooltipHeight: input.dimensions.height,
      }) + input.viewportState.scrollTop,
  };
}
